import { useEffect, useMemo, useRef, useState } from "react";
import { useAlert } from "../../contexts/AlertContext";
import { patchData, postData } from "../../services/api/api-tools";
import {
  CheckCircleIcon, Cog6ToothIcon,
  ExclamationTriangleIcon, HandThumbDownIcon, HandThumbUpIcon,
  SparklesIcon,
  ViewfinderCircleIcon, XCircleIcon
} from "@heroicons/react/20/solid";
import { ImageWithBbox } from "../gallery/snapshots/ImageWithBbox";
import { ActionButton } from "../../components/buttons/ActionButton";
import { Popover, PopoverTrigger, PopoverContent } from "@heroui/react";
import { QualityReferences } from "./components/QualityReferences";

export const QualityPage = () => {
  const IMAGES_NUMBER_TO_SEND = 1;

  const { showAlert } = useAlert();

  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const [videoRatio, setVideoRatio] = useState(null);
  const currentDeviceIndex = useRef(null);
  const videoDevices = useRef([]);
  const [sizes, setSizes] = useState({
    container: { width: 0, height: 0 },
    video: { width: 0, height: 0 },
  });
  const canvasRef = useRef(null);
  const [isSending, setIsSending] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState(0);
  const latestFile = useRef(null);
  const [latestBlobUrl, setLatestBlobUrl] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [pieces, setPieces] = useState([]);
  const [isImageValid, setIsImageValid] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [displayedSnapshotId, setDisplayedSnapshotId] = useState(null);
  const [referencesIds, setReferencesIds] = useState([]);
  const [selectedReferenceId, setSelectedReferenceId] = useState(null);

  const [videoDevicesState, setVideoDevicesState] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [errorText, setErrorText] = useState(null);

  /* CAMERA MANAGEMENT */

  const stopCurrentStream = () => {
    const stream = videoRef.current?.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
  };

  const handleError = (error) => {
    setErrorText(error.toString());
    console.error('navigator.MediaDevices.getUserMedia error: ', error, error.constraint, error.message);
  };

  const gotStream = (stream) => {
    stopCurrentStream();
    videoRef.current.srcObject = stream;
  };

  const getVideoDevices = async () => {
    const devices = await navigator.mediaDevices.enumerateDevices().catch(handleError);
    const cameras = devices.filter((device) => device.kind === 'videoinput');
    videoDevices.current = cameras;
    setVideoDevicesState(cameras);
    return cameras;
  };

  const startCamera = async (deviceId = null) => {
    const constraints = {
      video: deviceId
        ? {
          deviceId: { exact: deviceId },
          width: { ideal: 10000 },
          height: { ideal: 10000 },
        }
        : {
          facingMode: { ideal: 'environment' },
          width: { ideal: 10000 },
          height: { ideal: 10000 },
        },
    };

    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    gotStream(stream);

    const activeDeviceId = stream.getVideoTracks?.()[0]?.getSettings?.().deviceId;
    if (activeDeviceId) {
      setSelectedDeviceId(activeDeviceId);
    }

    return stream;
  };

  const handleCameraChange = async (event) => {
    const nextDeviceId = event.target.value;
    setSelectedDeviceId(nextDeviceId);

    try {
      await startCamera(nextDeviceId);
    }
    catch (error) {
      handleError(error);
    }
  };

  useEffect(() => {
    const initCamera = async () => {
      try {
        await startCamera();
        await getVideoDevices();
      }
      catch (error) {
        handleError(error);
      }
    };

    initCamera();

    return () => {
      stopCurrentStream();
    };
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    const video = videoRef.current;

    if (!container || !video) return;

    const updateSizes = () => {
      setSizes({
        container: {
          width: container.offsetWidth,
          height: container.offsetHeight,
        },
        video: {
          width: video.offsetWidth,
          height: video.offsetHeight,
        },
      });
    };

    // Initial call
    updateSizes();

    const observer = new ResizeObserver(updateSizes);
    observer.observe(container);
    observer.observe(video);

    return () => {
      observer.disconnect();
    };
  }, []);

  const coverSize = useMemo(() => {
    if (videoRatio === null)
      return { width: undefined, height: undefined, offsetX: undefined, offsetY: undefined };

    const containerRatio = sizes.container.width / sizes.container.height;

    let finalWidth, finalHeight;

    if (videoRatio > containerRatio) { // Larger video
      finalHeight = sizes.container.height;
      finalWidth = sizes.container.height * videoRatio;
    } else { // Larger container
      finalWidth = sizes.container.width;
      finalHeight = sizes.container.width / videoRatio;
    }

    if (isNaN(finalWidth) || isNaN(finalHeight))
      return { width: undefined, height: undefined, offsetX: undefined, offsetY: undefined };

    const offsetX = (sizes.container.width - finalWidth) / 2;
    const offsetY = (sizes.container.height - finalHeight) / 2;

    return {
      width: finalWidth,
      height: finalHeight,
      offsetX,
      offsetY,
    };
  }, [sizes]);

  const handleVideoLoad = () => {
    const video = videoRef.current;
    if (video) {
      console.log("Taille native :", video.videoWidth, "x", video.videoHeight);
      setVideoRatio(video.videoWidth / video.videoHeight);
    }
  };

  /* AUXILIARY FUNCTIONS */

  const generateSnapshotFilename = (now) => {
    const pad = (n, length = 2) => String(n).padStart(length, '0');

    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());
    const ms = pad(now.getMilliseconds(), 6);

    return `quality_1_${year}_${month}_${day}_${hours}_${minutes}_${seconds}_${ms}_`;
  };

  /* MAIN FUNCTION */

  const takeSnapshot = () => {
    setIsSending(true);
    setAnalysisProgress(0);
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const width = video.videoWidth;
    const height = video.videoHeight;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    setAnalysisProgress(20);

    // setTimeout allows UX refresh before actually performing actions
    setTimeout(() => {
      ctx.drawImage(video, 0, 0, width, height);

      setAnalysisProgress(30);
      showAlert('info', 'La photo a correctement été enregistrée. Le transfert est en cours et se fait en arrière-plan.', 'full-top');

      // setTimeout allows UX refresh before actually performing actions
      setTimeout(() => {
        canvas.toBlob(async (latestBlob) => {
          if (!latestBlob) return;

          try{
            setAnalysisProgress(40);

            /* Upload image */
            const buffer = snapshotsRef.current;
            if (buffer.length >= IMAGES_NUMBER_TO_SEND) buffer.shift();
            buffer.push(latestBlob);

            const now = new Date();
            const snapshotName = generateSnapshotFilename(now);

            for (let i = 0; i < buffer.length; i++) {
              const formData = new FormData();
              const file = new File([buffer[i]], snapshotName + i + '.jpg', { type: 'image/jpg' });
              formData.append('file', file);

              const response = await fetch(process.env.REACT_APP_API_ADDRESS + 'upload', {
                method: 'POST',
                body: formData,
              });
              const result = await response.json();
            }

            setAnalysisProgress(70);

            /* Perform analysis */
            latestFile.current = new File([buffer[buffer.length - 1]], 'image');
            const data = await performAnalysis();

            setAnalysisProgress(80);

            /* Save results */
            const saveResponse = await postData('quality_snapshots', {
              date: Math.floor(now.getTime() / 1000),
              images_number: buffer.length,
              file_name: snapshotName + parseInt(buffer.length - 1) + '.jpg',
              bboxes_to_check: data,
            }, true);
            console.log(saveResponse);
            setDisplayedSnapshotId(saveResponse.id);

            setAnalysisProgress(100);

            setPieces(data.pieces);
            setIsImageValid(data.image_quality === 0);
            setHasErrors(!data.pieces.every((piece) => piece.conform));
            setShowResults(true);

            // showAlert('success', 'Le transfert de la photo est terminée. Elle est maintenant disponible dans la galerie.', 'full-top');
          }
          catch (error) {
            showAlert('error', `Une erreur est survenue lors du traitement de la photo (Détails: ${error}).`, 'full-top');
            console.error(error);
          }
          finally {
            setIsSending(false);
          }
        }, 'image/jpg'); // TODO
      }, 30);
    }, 30);
  };

  const snapshotsRef = useRef([]);

  const performAnalysis = async (referenceId = null) => {
    // Perform analysis
    const url = `${process.env.REACT_APP_ADDRESS}services/analyze_image`;
    const formData = new FormData();

    let params = {};
    if (referenceId) params = {...params, reference_id: referenceId};
    formData.append('params', JSON.stringify(params));
    formData.append('image', latestFile.current);

    const blobUrl = URL.createObjectURL(latestFile.current);
    setLatestBlobUrl(blobUrl);

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });
    const data = await response.json();
    // TODO REMOVE
    // let data = await response.json();
    // data = {...data, references_ids: ['toto', 'tata', 'titi'], reference_id: 'titi'}
    setReferencesIds(data?.references_ids ?? []);
    setSelectedReferenceId(data?.reference_id ?? null);
    setErrorText(data?.message ?? null);

    console.log(data);
    return data;
  };

  const createReference = async () => {
    try {
      const url = `${process.env.REACT_APP_ADDRESS}services/set_reference`;
      const formData = new FormData();
      formData.append('params', {});

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      console.log(data);

      showAlert('success', 'La référence a été créée.', 'full-top');
      restartProcess();
      return data;
    } catch (error) {
      console.error('Error saving pattern on database', error);
      showAlert('error', `Une erreur est survenue lors de la création de la référence (Détails: ${error}).`, 'full-top');
      return null;
    }
  };

  // Uncomment this useEffect to record multiple images
  /*useEffect(() => {
    const captureImage = () => {
      if (isSending) return;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (!video || !canvas) return;

      const width = video.videoWidth;
      const height = video.videoHeight;

      canvas.width = width * 0.5;
      canvas.height = height * 0.5;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.drawImage(video, 0, 0, width * 0.5, height * 0.5);

      canvas.toBlob((blob) => {
        if (blob) {
          const buffer = snapshotsRef.current;
          if (buffer.length >= IMAGES_NUMBER_TO_SEND) {
            buffer.shift();
          }
          buffer.push(blob);
        }
      }, 'image/jpg');
    };

    const intervalId = setInterval(captureImage, 1000);

    return () => clearInterval(intervalId);
  }, [videoRef, canvasRef, isSending]);*/

  const restartProcess = () => {
    setShowResults(false);
    setPieces([]);
    setAnalysisProgress(0);
    setIsSending(false);
    setDisplayedSnapshotId(null);
  };

  const handleEvaluateAI = async (userValidation) => {
    try {
      const response = await patchData(`quality_snapshots/${displayedSnapshotId}`, {
        user_validation: userValidation
      });
      showAlert('success', 'Merci pour votre retour !', 'full-top');
      if (response) return response;
    } catch (error) {
      console.error('Error saving pattern on database', error);
      showAlert('error', `Une erreur est survenue lors du traitement du retour (Détails: ${error}).`, 'full-top');
      return null;
    }
  };

  const styles = {
    overlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      right: 0,
      // backgroundColor: 'rgba(0, 0, 0, 0.3)',
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      maskImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='100%' height='100%'><defs><mask id='mask' x='0' y='0' width='100%' height='100%'><rect width='100%' height='100%' fill='white'/><rect x='4%' y='3%' width='92%' height='94%' rx='20' ry='20' fill='black'/></mask></defs><rect width='100%' height='100%' mask='url(%23mask)' fill='white'/></svg>")`,
      pointerEvents: 'none',
      zIndex: 50,
      backdropFilter: 'blur(10px)',
    },
    frameCorner: {
      position: 'absolute',
      width: '50px',
      height: '50px',
      border: '7px solid white',
      borderRadius: '20px',
      zIndex: 51,
      pointerEvents: 'none',
    },
    messagePopup: {
      width: '70%',
      height: '33%',
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      zIndex: 50,
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      backdropFilter: 'blur(10px)',
      borderRadius: '20px',
    },
    resultPopup: {
      width: '100%',
      height: '100%',
      position: 'absolute',
      left: 0,
      top: 0,
      zIndex: 60,
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      backdropFilter: 'blur(10px)',
      overflow: 'hidden',
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="h-full bg-gray relative overflow-hidden" ref={containerRef}>

        {/* Video element with display/resize functions */}
        <video
          ref={videoRef}
          autoPlay
          muted
          onLoadedMetadata={handleVideoLoad}
          className="max-w-none max-h-none"
          style={{
            width: coverSize.width,
            height: coverSize.height,
            transform: `translate(${coverSize.offsetX}px, ${coverSize.offsetY}px)`
          }}
        />

        {/* Canvas used to take snapshot */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />

        {/* Overlay */}
        {
          showResults ?
            <div className="flex justify-center items-center" style={styles.resultPopup}>
              <div className="flex flex-col justify-center w-[90%] h-[90%] rounded-[20px] overflow-hidden relative">
                {/* Image taken */}
                <ImageWithBbox pieces={pieces} src={latestBlobUrl} />

                {/* References block */}
                {
                  (!errorText && isImageValid) &&
                    <QualityReferences
                      referencesIds={referencesIds} selectedReferenceId={selectedReferenceId}
                      onSelectReference={performAnalysis} createReference={createReference}
                    />
                }

                {/* Bottom part */}
                <div className="bg-white min-h-32 flex justify-between items-center gap-4 p-4 text-center">
                  <div className="flex-1 flex justify-center">
                  {
                    errorText ?
                      <div className="rounded-md bg-yellow-50 p-4">
                        <div className="flex justify-center items-center">
                          <ExclamationTriangleIcon aria-hidden="true" className="size-5 text-yellow-400" />
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">
                              {errorText}
                            </h3>
                          </div>
                        </div>
                      </div>
                    : !isImageValid ?
                      <div className="rounded-md bg-yellow-50 p-4">
                        <div className="flex justify-center items-center">
                          <ExclamationTriangleIcon aria-hidden="true" className="size-5 text-yellow-400" />
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">
                              Impossible de détecter les pièces.<br/>Assurez-vous que l'image ne soit pas floue.
                            </h3>
                          </div>
                        </div>
                      </div>
                      : hasErrors ?
                        <div className="rounded-md bg-red-50 p-4">
                          <div className="flex items-center">
                            <XCircleIcon aria-hidden="true" className="size-5 text-red-400" />
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-red-800">
                                Une ou plusieurs erreurs de collage ont été détectées.<br/>Merci de vérifier les pièces marquées.
                              </h3>
                            </div>
                          </div>
                        </div>
                      :
                        <div className="rounded-md bg-green-50 p-4">
                          <div className="flex items-center">
                            <CheckCircleIcon aria-hidden="true" className="size-5 text-green-400" />
                            <div className="ml-3">
                              <h3 className="text-sm font-medium text-green-800">
                                L'ensemble des pièces est conforme.
                              </h3>
                            </div>
                          </div>
                        </div>
                  }
                  </div>
                  {
                    (!errorText && isImageValid) &&
                      <div className="min-w-48 flex flex-col gap-2 w-1/4">
                        <ActionButton text="Résultat correct" Icon={HandThumbUpIcon} color="success" onClick={() => handleEvaluateAI(true)}/>
                        <ActionButton text="Résultat incorrect" Icon={HandThumbDownIcon} color="danger" onClick={() => handleEvaluateAI(false)}/>
                      </div>
                  }
                </div>
              </div>
            </div>
            :
            <>
              <div style={styles.overlay} />
              <div style={{...styles.frameCorner, borderBottom: 'none', borderRight: 'none', borderBottomLeftRadius: 0, borderTopRightRadius: 0, top: '3%', left: '4%'}} />
              <div style={{...styles.frameCorner, borderBottom: 'none', borderLeft: 'none', borderBottomRightRadius: 0, borderTopLeftRadius: 0, top: '3%', right: '4%'}} />
              <div style={{...styles.frameCorner, borderTop: 'none', borderLeft: 'none', borderBottomLeftRadius: 0, borderTopRightRadius: 0, bottom: '3%', right: '4%'}} />
              <div style={{...styles.frameCorner, borderTop: 'none', borderRight: 'none', borderBottomRightRadius: 0, borderTopLeftRadius: 0, bottom: '3%', left: '4%'}} />
            </>
        }

        {
          isSending &&
            <div style={styles.messagePopup} className="flex flex-col gap-8 justify-center items-center text-2xl p-8 text-center text-perception-gray-700">
              Transfert et analyse en cours...
              <style>{`@keyframes bounceScale {0%, 100% {transform: scale(1);} 50% {transform: scale(1.1);}}`}</style>
              <ViewfinderCircleIcon className="size-24 animate-[bounceScale_3s_ease-in-out_infinite] text-white "/>
              <div className="w-full bg-white h-3 rounded-full overflow-hidden">
                <div id="progress-bar" className="h-full bg-perception-blue transition-all duration-500 animate-[bounceScale_3s_ease-in-out_infinite]" style={{ width: `${analysisProgress}%` }}/>
              </div>
            </div>
        }
      </div>

      {/* Snapshot button */}
      <div className="relative h-[15%] border-t border-perception-black-900">
        {
          showResults ?
            <div className="flex justify-center items-center h-full">
              <button className="bg-perception-blue text-white p-4 rounded-md flex items-center" onClick={restartProcess}>
                <SparklesIcon aria-hidden="true" className="size-5 mr-2" /> Recommencer
              </button>
            </div>
            :
            <>
              <div className="absolute bg-white w-16 h-16 rounded-full p-1 left-[50%] top-[50%] -ml-8 -mt-8">
                <div className="bg-perception-gray-500 w-full h-full rounded-full p-0.5">
                  <div className={`w-full h-full rounded-full ${isSending ? 'bg-gray-300' : 'bg-white'}`} onClick={takeSnapshot}/>
                </div>
              </div>
              {
                isSending &&
                  <div className="absolute left-[50%] top-[50%] -ml-8 -mt-8">
                    <svg aria-hidden="true" className="w-16 h-16 text-white animate-[spin_0.75s_linear_infinite] fill-perception-blue" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
                    </svg>
                  </div>
              }
            </>
        }
        <div className="absolute top-2 left-2 z-10">

        </div>
        <div className="absolute right-8 top-1/2 -translate-y-1/2">
          <Popover placement="top">
            <PopoverTrigger>
              <Cog6ToothIcon className="text-perception-gray-500 size-8"/>
            </PopoverTrigger>
            <PopoverContent>
              <div className="p-4">
                <select value={selectedDeviceId} onChange={handleCameraChange} className="bg-transparent">
                  {
                    videoDevicesState.map((device, index) => (
                      <option key={device.deviceId || index} value={device.deviceId}>
                        {device.label || `Camera ${index + 1}`}
                      </option>
                    ))
                  }
                </select>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
};
